import { ReactNode } from 'react';
import { Module, Permission } from '@/config/permissions';
import { usePermissions } from '@/hooks/usePermissions';
import { Lock } from 'lucide-react';
import { Card } from '@/components/common/Card';

interface PermissionGuardProps {
  module: Module;
  permission?: Permission;
  children: ReactNode;
  fallback?: ReactNode;
  showMessage?: boolean;
}

export function PermissionGuard({
  module,
  permission = 'read',
  children,
  fallback,
  showMessage = true,
}: PermissionGuardProps) {
  const { checkPermission, userRole } = usePermissions(module);
  const hasPermission = checkPermission(permission);

  if (!hasPermission) {
    if (fallback) return <>{fallback}</>;
    if (showMessage) {
      return (
        <Card className="p-8 text-center border border-red-200 bg-red-50">
          <Lock className="w-12 h-12 mx-auto mb-4 text-red-600" />
          <h3 className="text-xl font-semibold text-gray-900 mb-2">
            Access Denied
          </h3>
          <p className="text-gray-600 mb-4">
            Your role ({userRole}) does not have permission to {permission} this module.
          </p>
          <p className="text-sm text-gray-500">
            Please contact your administrator if you need access.
          </p>
        </Card>
      );
    }
    return null;
  }

  return <>{children}</>;
}

interface ConditionalRenderProps {
  module: Module;
  permission: Permission;
  children: ReactNode;
}

export function ConditionalRender({ module, permission, children }: ConditionalRenderProps) {
  const { checkPermission } = usePermissions(module);
  if (!checkPermission(permission)) return null;
  return <>{children}</>;
}

interface RoleBasedContentProps {
  admin?: ReactNode;
  abmManager?: ReactNode;
  abmOperations?: ReactNode;
  mda?: ReactNode;
  sales?: ReactNode;
  default?: ReactNode;
}

export function RoleBasedContent({
  admin,
  abmManager,
  abmOperations,
  mda,
  sales,
  default: defaultContent,
}: RoleBasedContentProps) {
  const { userRole } = usePermissions();

  switch (userRole) {
    case 'ADMINISTRATOR':
      return <>{admin || defaultContent}</>;
    case 'ABM_MANAGER':
      return <>{abmManager || defaultContent}</>;
    case 'ABM_OPERATIONS':
      return <>{abmOperations || defaultContent}</>;
    case 'MDA':
      return <>{mda || defaultContent}</>;
    case 'SALES':
      return <>{sales || defaultContent}</>;
    default:
      return <>{defaultContent}</>;
  }
}

export default PermissionGuard;
